import React from "react";
import { Button, Dropdown } from "react-bootstrap";

interface PaginationProps {
  pageIndex: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  pageIndex,
  pageSize,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 30, 50],
}) => {
  const totalPages = Math.ceil(totalRecords / pageSize);
  const maxPageButtons = 5;

  const getPageNumbers = React.useMemo(() => {
    const pages: number[] = [];
    if (totalPages <= maxPageButtons) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else if (pageIndex <= 2) {
      pages.push(0, 1, 2, -1, totalPages - 1);
    } else if (pageIndex >= totalPages - 3) {
      pages.push(0, -1, totalPages - 3, totalPages - 2, totalPages - 1);
    } else {
      pages.push(
        0,
        -1,
        pageIndex - 1,
        pageIndex,
        pageIndex + 1,
        -1,
        totalPages - 1
      );
    }
    return pages;
  }, [pageIndex, totalPages]);

  return (
    <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between w-100">
      {/* {`<style>
          select option:hover {
  background-color: #0073BB;
  // color: #fff;
}
        </style>`} */}
      {/* Results Info */}
      <div className="text-muted">
        Showing{" "}
        <span className="fw-semibold">
          {totalRecords === 0 ? 0 : pageIndex * pageSize + 1}
        </span>{" "}
        -{" "}
        <span className="fw-semibold">
          {Math.min((pageIndex + 1) * pageSize, totalRecords)}
        </span>{" "}
        of <span className="fw-semibold">{totalRecords}</span> Results
      </div>

      {/* Page Navigation */}
      <div className="d-flex flex-wrap align-items-center gap-2">
        <ul className="pagination pagination-separated mb-0">
          {/* Previous */}
          <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
            <Button
              variant="link"
              className="page-link"
              disabled={pageIndex === 0}
              onClick={() => onPageChange(pageIndex - 1)}
            >
              &lt;
            </Button>
          </li>

          {/* Page numbers */}
          {getPageNumbers.map((page, idx) => (
            <li key={idx} className="page-item">
              {page === -1 ? (
                <span className="page-link">...</span>
              ) : (
                <Button
                  variant="link"
                  className={`page-link ${pageIndex === page ? "active" : ""}`}
                  onClick={() => onPageChange(page)}
                >
                  {page + 1}
                </Button>
              )}
            </li>
          ))}

          {/* Next */}
          <li
            className={`page-item ${
              pageIndex >= Math.ceil(totalRecords / pageSize) - 1
                ? "disabled"
                : ""
            }`}
          >
            <Button
              variant="link"
              className="page-link"
              disabled={pageIndex >= Math.ceil(totalRecords / pageSize) - 1}
              onClick={() => onPageChange(pageIndex + 1)}
            >
              &gt;
            </Button>
          </li>
        </ul>

        {/* Page Size Select */}
        <div className="ms-2">
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              variant="secondary"
              // className="border-black"
              style={{ minWidth: "100px" }}
            >
              Show {pageSize}
            </Dropdown.Toggle>

            <Dropdown.Menu style={{ minWidth: "100px" }}>
              {pageSizeOptions.map((size) => (
                <Dropdown.Item
                  key={size}
                  active={pageSize === size}
                  onClick={() => {
                    onPageChange(0);
                    onPageSizeChange(size);
                  }}
                  style={{
                    backgroundColor: pageSize === size ? "#0073bb" : "#fff",
                    color: pageSize === size ? "#fff" : "#000",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#0073bb";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    if (pageSize === size) {
                      e.currentTarget.style.backgroundColor = "#0073bb";
                      e.currentTarget.style.color = "#fff";
                    } else {
                      e.currentTarget.style.backgroundColor = "#fff";
                      e.currentTarget.style.color = "#000";
                    }
                  }}
                >
                  Show {size}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
